import {degreeWords} from './size-questions';

/**
 * The redesign's generated words, pure so they are unit-tested (copy.test.ts). Ship days follow today's page (the defaults
 * Jill or Steve confirm, PRODUCT-PAGE-REDESIGN-AB-TEST.md): 5-7 business days from the Shipping section, 2-3 days for Fast Ship
 * from the legend.
 */
export const STANDARD_SHIP_DAYS = '5-7 business days';
export const FAST_SHIP_DAYS = '2-3 days';

/** A size in inches, as the artboards write it: 14.25" x 11.25". */
export const inches = (width: number, height: number): string => `${Number(width)}" x ${Number(height)}"`;

export type ShipState = 'fast' | 'changed' | 'standard';

/**
 * C1 every pick is the Fast Ship setup; C2 the item has Fast Ship but a tagged pick is not; C3 the item has no Fast Ship. A school,
 * seal or size that is not the product's own has no tag to go back to (tagsDecide false), so that case reads as C3.
 */
export const shipState = (fastShipItem: boolean, fastShipNow: boolean, tagsDecide: boolean = true): ShipState => (!fastShipItem ? 'standard' : fastShipNow ? 'fast' : tagsDecide ? 'changed' : 'standard');

/** The ship line under Add To Cart: the bold part and the hint after it. */
export const shipLine = (state: ShipState): {text: string; hint: string} =>
    state === 'fast' ? {text: `Fast Ship: ships in ${FAST_SHIP_DAYS}.`, hint: ''} : state === 'changed' ? {text: `Ships in ${STANDARD_SHIP_DAYS}.`, hint: `Pick the options marked Fast Ship to get it in ${FAST_SHIP_DAYS}.`} : {text: `Ships in ${STANDARD_SHIP_DAYS}.`, hint: ''};

/** The pinned bar's short ship note. */
export const shipShort = (state: ShipState): string => (state === 'fast' ? `Fast Ship: ${FAST_SHIP_DAYS}` : `Ships in ${STANDARD_SHIP_DAYS}`);

export const questionsLeft = (count: number): string => `${count} quick question${count === 1 ? '' : 's'} left to set your size`;

/** "Answer step 2", "Answer steps 2 and 3", "Answer steps 1, 2 and 3". */
export const answerSteps = (numbers: number[]): string => {
    if (numbers.length === 1) return `Answer step ${numbers[0]}`;
    return `Answer steps ${numbers.slice(0, -1).join(', ')} and ${numbers[numbers.length - 1]}`;
};

export type FitInput = {
    /** si.ProdInstText ("NYU"), else the institution's name. */
    school: string;
    /** ui.doc_type ("Diploma"). */
    docType: string;
    /** The size row's DocSizeName, else the product's DegreeType ('' when neither). */
    degreeName: string;
    width: number;
    height: number;
};

/** "Made to fit your NYU diploma." and "Bachelor's, Master's and PhD, 14.25" x 11.25"." */
export const fitText = (input: FitInput): {lead: string; detail: string} => {
    const doc = (input.docType || 'document').trim().toLowerCase();
    const school = (input.school || '').trim();
    const degrees = input.degreeName ? degreeWords(input.degreeName) : '';
    const size = inches(input.width, input.height);
    return {
        lead: `Made to fit your ${school ? `${school} ` : ''}${doc}.`,
        detail: `${degrees ? `${degrees}, ` : ''}${size}.`,
    };
};

/** The picks, for the pinned bar and the phone's "Your frame:" line, skipping the ones the page does not offer. */
export const picksSummary = (parts: (string | null | undefined)[]): string =>
    parts
        .map(p => (p || '').trim())
        .filter(p => p !== '')
        .join(', ');
